import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useComplianceAlerts } from '@/hooks/useComplianceAlerts';
import { useCheckInData } from '@/hooks/useCheckInData';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { Button } from '@/components/ui/button';
import { PageTransition } from '@/components/PageTransition';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import {
  Users,
  Clock,
  CheckCircle,
  BarChart3,
  UserPlus,
  Download,
  MapPin,
  Plus,
  Loader2,
  Camera,
  Timer
} from 'lucide-react';
import { TOOL_REGISTRY as toolConfigs } from '@/config/tools';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { CheckInsSidebar } from '@/components/CheckInsSidebar';
import { GeofenceSettings } from '@/components/GeofenceSettings';
import { NFCTagWriter } from '@/components/NFCTagWriter';
import BreakTypesManager from '@/pages/checkin/BreakTypesManager';
import KiosksView from '@/pages/checkin/KiosksView';
import TimesheetsView from '@/pages/checkin/TimesheetsView';
import DashboardView from '@/pages/checkin/dashboard/DashboardView';
import LiveComplianceView from '@/pages/checkin/compliance/LiveComplianceView';

// Auto clock-out settings component
function AutoClockOutSettings({ siteLocation }) {
  const [enabled, setEnabled] = useState(
    siteLocation?.auto_clock_out_hours != null && siteLocation.auto_clock_out_hours > 0
  );
  const [hours, setHours] = useState(siteLocation?.auto_clock_out_hours || 12);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const val = siteLocation?.auto_clock_out_hours;
    setEnabled(val != null && val > 0);
    if (val > 0) setHours(val);
  }, [siteLocation?.auto_clock_out_hours]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('site_locations')
        .update({ auto_clock_out_hours: enabled ? hours : null })
        .eq('id', siteLocation.id);

      if (error) throw error;
      toast.success('Auto clock-out settings saved');
    } catch (err) {
      console.error('Error saving auto clock-out settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Timer className="h-5 w-5 text-blue-600" />
          Auto Clock-Out
        </CardTitle>
        <CardDescription>
          Automatically clock people out if they forget to do so manually
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="auto-clock-out-toggle" className="text-sm font-medium">
              Enable auto clock-out
            </Label>
            <p className="text-xs text-gray-500 mt-0.5">
              People will be automatically clocked out after the set duration
            </p>
          </div>
          <Switch
            id="auto-clock-out-toggle"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        {enabled && (
          <div className="flex items-center gap-3 pt-2">
            <Label htmlFor="auto-clock-out-hours" className="text-sm text-gray-600 whitespace-nowrap">
              Clock out after
            </Label>
            <input
              id="auto-clock-out-hours"
              type="number"
              min={1}
              max={24}
              step={0.5}
              value={hours}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val)) setHours(Math.max(1, Math.min(24, val)));
              }}
              className="w-20 h-9 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700 tabular-nums text-center focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
            />
            <span className="text-sm text-gray-600">hours</span>
          </div>
        )}

        <Button
          onClick={handleSave}
          disabled={saving}
          size="sm"
          className="mt-2"
        >
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save
        </Button>
      </CardContent>
    </Card>
  );
}

// Settings content component with ability to create site location
function CheckInSettingsContent({ siteLocation, projectId, companyId, spaceName, onRefresh }) {
  const [creating, setCreating] = useState(false);
  const [localSiteLocation, setLocalSiteLocation] = useState(siteLocation);

  // Update local state when prop changes
  useEffect(() => {
    setLocalSiteLocation(siteLocation);
  }, [siteLocation]);

  const createSiteLocation = async () => {
    if (!projectId || !companyId) return;

    setCreating(true);
    try {
      // Generate a unique QR code
      const qrCode = `site_${projectId}_${Date.now()}`;

      const { data, error } = await supabase
        .from('site_locations')
        .insert({
          project_id: projectId,
          company_id: companyId,
          name: spaceName || 'Site Location',
          qr_code_data: qrCode,
          geofence_enabled: false,
          geofence_enforcement: 'off',
          geofence_radius_meters: 100
        })
        .select()
        .single();

      if (error) throw error;

      setLocalSiteLocation(data);
      onRefresh?.();
      toast.success('Site location created');
    } catch (error) {
      console.error('Error creating site location:', error);
      toast.error('Failed to create site location');
    } finally {
      setCreating(false);
    }
  };

  if (localSiteLocation) {
    const checkInUrl = `${window.location.origin}/checkin/${localSiteLocation.qr_code_data}`;

    return (
      <div className="space-y-6">
        <GeofenceSettings
          siteLocationId={localSiteLocation.id}
          initialData={localSiteLocation}
          onSave={() => {
            onRefresh?.();
            toast.success('Settings saved successfully');
          }}
        />

        {/* Auto Clock-Out Settings */}
        <AutoClockOutSettings siteLocation={localSiteLocation} />

        {/* Break Types Management */}
        <BreakTypesManager companyId={companyId} />

        {/* NFC Tag Writer */}
        <NFCTagWriter
          checkInUrl={checkInUrl}
          siteName={localSiteLocation.name || spaceName}
        />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-blue-600" />
          Geofence Settings
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8">
          <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-gray-900">No site location configured</p>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Create a site location to enable geofencing and QR code check-ins.
          </p>
          <Button onClick={createSiteLocation} disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create Site Location
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export const CheckInPage = ({
  isStackView = false,
  stackId = null,
  stackName = null,
  stackProjects = [],
  selectedSpaceFilter = 'all',
  onSpaceFilterChange = null
} = {}) => {
  const { projectId: paramProjectId, stackId: paramStackId } = useParams();
  const { userProfile, company } = useAuth();

  // Set page title
  usePageTitle(isStackView ? `${stackName} Check-ins` : 'Check-ins');

  // Use props if in stack view, otherwise use URL params
  const projectId = isStackView ? null : paramProjectId;
  const activeStackId = isStackView ? stackId : paramStackId;

  // Local UI state
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showTimesheets, setShowTimesheets] = useState(false);
  const [showLiveCompliance, setShowLiveCompliance] = useState(false);
  const [showManualCheckInDialog, setShowManualCheckInDialog] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showKiosks, setShowKiosks] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selfieViewUrl, setSelfieViewUrl] = useState(null); // Selfie image dialog

  // Manual check-in states
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [manualCheckInNotes, setManualCheckInNotes] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Use optimized hook for data fetching
  const {
    project: space,
    siteLocation,
    onSiteUsers,
    recentCheckIns: checkIns,
    activityFeed,
    analyticsData,
    stats,
    gapAnalysis,
    getEmployeeStats,
    loading,
    statsLoading,
    handleCheckOut,
    handleCheckIn,
    refreshOnSiteUsers,
    totalOnSite,
    gapLoading
  } = useCheckInData(projectId, {
    projectIds: stackProjects?.map(p => p.id),
    isStackView,
    selectedSpaceFilter,
    enabled: !!(projectId || (isStackView && stackProjects?.length > 0))
  });

  // Derive spaceTools from space data
  const spaceTools = useMemo(() => {
    if (!space?.tools) return [];
    const toolsObj = typeof space.tools === 'string' ? JSON.parse(space.tools) : space.tools;
    return Object.entries(toolsObj)
      .filter(([, enabled]) => enabled === true)
      .map(([toolKey]) => ({
        id: toolKey,
        tool_type: toolKey,
        name: toolConfigs[toolKey]?.name || toolKey,
        icon: toolConfigs[toolKey]?.icon,
        color: toolConfigs[toolKey]?.color
      }));
  }, [space?.tools]);

  // Memoize gap analysis counts to avoid recalculating on every render
  const gapAnalysisCounts = useMemo(() => {
    const shortage = gapAnalysis.filter(g => g.status === 'shortage');
    const met = gapAnalysis.filter(g => g.status === 'met' || g.status === 'excess');
    return {
      shortageCount: shortage.length,
      shortageItems: shortage,
      metCount: met.length
    };
  }, [gapAnalysis]);

  // Compliance alerts hook - only for single project view
  const {
    settings: alertSettings,
    analyzeGapChanges,
    requestNotificationPermission
  } = useComplianceAlerts(userProfile?.id, projectId);

  // Request notification permission on mount if enabled
  useEffect(() => {
    if (alertSettings?.show_browser_notifications) {
      requestNotificationPermission();
    }
  }, [alertSettings, requestNotificationPermission]);

  // Real-time subscription for check-ins
  useEffect(() => {
    if (isStackView || !projectId) return;

    const channelName = `project-checkins-${projectId}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_checkins',
          filter: `project_id=eq.${projectId}`
        },
        () => {
          // Refresh data when any change occurs
          refreshOnSiteUsers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, isStackView, refreshOnSiteUsers]);

  // Trigger compliance alerts when gap analysis changes
  useEffect(() => {
    if (analyzeGapChanges && gapAnalysis.length > 0) {
      analyzeGapChanges(gapAnalysis);
    }
  }, [gapAnalysis, analyzeGapChanges]);

  // Manual checkout is handled by the hook
  const handleManualCheckOut = useCallback(async (checkInId) => {
    await handleCheckOut(checkInId);
  }, [handleCheckOut]);

  const fetchCompanyUsers = useCallback(async () => {
    if (!company?.id) return;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, display_name, email, avatar_url, department')
        .eq('company_id', company.id)
        .order('first_name', { ascending: true });

      if (error) throw error;

      setAllUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  }, [company?.id]);

  const handleOpenManualCheckIn = useCallback(() => {
    fetchCompanyUsers();
    setShowManualCheckInDialog(true);
  }, [fetchCompanyUsers]);

  const downloadQRCode = useCallback(() => {
    const svg = document.getElementById('space-checkin-qr');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${space?.name || 'space'}-checkin-qr.png`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('QR code downloaded');
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }, [space?.name]);

  // Manual check-in using hook
  const handleManualCheckInSubmit = useCallback(async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    const success = await handleCheckIn(selectedUsers, manualCheckInNotes);
    if (success) {
      setSelectedUsers([]);
      setSearchQuery('');
      setManualCheckInNotes('');
      setShowManualCheckInDialog(false);
    }
  }, [selectedUsers, manualCheckInNotes, handleCheckIn]);

  const toggleUserSelection = useCallback((user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  }, []);

  // Filtered users with debounced search for better performance
  const filteredUsers = useMemo(() => {
    return allUsers.filter(user => {
      const searchLower = debouncedSearchQuery.toLowerCase();
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const displayName = (user.display_name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      const department = (user.department || '').toLowerCase();

      return fullName.includes(searchLower) ||
             displayName.includes(searchLower) ||
             email.includes(searchLower) ||
             department.includes(searchLower);
    });
  }, [allUsers, debouncedSearchQuery]);

  if (loading) {
    return (
      <AppShell3 project={space}>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3 project={space}>
      <PageTransition>
        <ToolHeader
          spaceName={isStackView ? stackName : (space?.name || 'Space')}
          toolName="Check-Ins"
          currentTool="check_ins"
          spaceTools={isStackView ? [] : spaceTools}
          stackId={activeStackId}
          stackProjects={isStackView ? stackProjects : []}
          isStackView={isStackView}
          selectedSpaceFilter={selectedSpaceFilter}
          onSpaceFilterChange={onSpaceFilterChange}
        />

        {/* Main layout with sidebar */}
        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <CheckInsSidebar
              totalOnSite={totalOnSite}
              showAnalytics={showAnalytics}
              showTimesheets={showTimesheets}
              showLiveCompliance={showLiveCompliance}
              complianceGapCount={gapAnalysisCounts.shortageCount}
              showSettings={showSettings}
              showDashboard={!showAnalytics && !showSettings && !showTimesheets && !showLiveCompliance && !showKiosks}
              onShowDashboard={() => {
                setShowAnalytics(false);
                setShowSettings(false);
                setShowTimesheets(false);
                setShowLiveCompliance(false);
                setShowKiosks(false);
              }}
              onToggleLiveCompliance={() => {
                setShowLiveCompliance(true);
                setShowAnalytics(false);
                setShowSettings(false);
                setShowTimesheets(false);
                setShowKiosks(false);
              }}
              onToggleTimesheets={() => {
                setShowTimesheets(true);
                setShowAnalytics(false);
                setShowSettings(false);
                setShowLiveCompliance(false);
                setShowKiosks(false);
              }}
              onToggleAnalytics={() => {
                setShowAnalytics(true);
                setShowSettings(false);
                setShowTimesheets(false);
                setShowLiveCompliance(false);
                setShowKiosks(false);
              }}
              showKiosks={showKiosks}
              onToggleKiosks={() => {
                setShowKiosks(true);
                setShowAnalytics(false);
                setShowSettings(false);
                setShowTimesheets(false);
                setShowLiveCompliance(false);
              }}
              onShowQRCode={() => setShowQRCode(true)}
              onOpenManualCheckIn={handleOpenManualCheckIn}
              isStackView={isStackView}
              onToggleSettings={() => {
                setShowSettings(true);
                setShowAnalytics(false);
                setShowTimesheets(false);
                setShowLiveCompliance(false);
                setShowKiosks(false);
              }}
            />
          </div>

          {/* Main Content */}
          <div className={cn("flex-1", (showTimesheets || showLiveCompliance || (!showAnalytics && !showSettings && !showKiosks)) ? "overflow-hidden" : "overflow-y-auto")}>
            {showKiosks ? (
              /* Kiosks View */
              <KiosksView
                siteLocationId={siteLocation?.id}
                projectId={projectId}
                companyId={company?.id}
              />
            ) : showTimesheets ? (
              /* Timesheets View */
              <TimesheetsView
                projectId={projectId}
                companyId={company?.id}
                onViewSelfie={(url) => setSelfieViewUrl(url)}
              />
            ) : showLiveCompliance ? (
              /* Live Compliance View */
              <LiveComplianceView
                gapAnalysis={gapAnalysis}
                gapAnalysisCounts={gapAnalysisCounts}
                gapLoading={gapLoading}
              />
            ) : showSettings ? (
              /* Settings View */
              <div className="container mx-auto px-6 py-6 max-w-4xl">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold tracking-tight">Check-In Settings</h2>
                  <p className="text-muted-foreground mt-1">
                    Configure geofencing and check-in settings for this space
                  </p>
                </div>

                <CheckInSettingsContent
                  siteLocation={siteLocation}
                  projectId={projectId}
                  companyId={company?.id}
                  spaceName={space?.name}
                  onRefresh={refreshOnSiteUsers}
                />
              </div>
            ) : showAnalytics ? (
              /* Analytics View */
              <div className="container mx-auto px-6 py-6 max-w-4xl">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold tracking-tight">Check-In Analytics</h2>
                  <p className="text-muted-foreground mt-1">
                    View check-in patterns and statistics for the last 30 days
                  </p>
                </div>

                {analyticsData ? (
                  <Card>
                    <CardContent className="p-6 space-y-6">
                      {/* Summary Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Total Check-Ins</p>
                          <p className="text-2xl font-bold text-gray-900">{analyticsData.totalCheckIns}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Completed</p>
                          <p className="text-2xl font-bold text-gray-900">{analyticsData.completedCheckIns}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Total Hours</p>
                          <p className="text-2xl font-bold text-gray-900">{analyticsData.totalHours}h</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Avg Duration</p>
                          <p className="text-2xl font-bold text-gray-900">{analyticsData.avgDuration}</p>
                        </div>
                      </div>

                      {/* Last 7 Days Activity */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-4">Daily Activity (Last 7 Days)</h4>
                        <div className="grid grid-cols-7 gap-2">
                          {analyticsData.last7Days.map((day, index) => {
                            const maxCount = Math.max(...analyticsData.last7Days.map(d => d.count));
                            const heightPercent = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                            return (
                              <div key={index} className="flex flex-col items-center gap-2">
                                <div className="w-full h-32 bg-gray-100 rounded-lg flex items-end p-1">
                                  <div
                                    className="w-full bg-blue-500 rounded transition-all"
                                    style={{ height: `${heightPercent}%` }}
                                    title={`${day.count} check-ins`}
                                  />
                                </div>
                                <div className="text-center">
                                  <p className="text-xs font-medium text-gray-700">{day.date}</p>
                                  <p className="text-xs text-gray-500">{day.count}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Peak Hours */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-4">
                          Peak Hours
                          <span className="text-sm font-normal text-gray-600 ml-2">
                            (Busiest: {analyticsData.peakHour})
                          </span>
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {analyticsData.topHours.map((hourData, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <Clock className="h-4 w-4 text-blue-600" />
                                </div>
                                <span className="font-medium text-gray-900">{hourData.label}</span>
                              </div>
                              <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                                {hourData.count}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-6">
                      <div className="text-center py-12 text-gray-500">
                        <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <p className="font-medium">No data available</p>
                        <p className="text-sm mt-1">Analytics will appear once check-ins are recorded</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              /* Dashboard View */
              <DashboardView
                onSiteUsers={onSiteUsers}
                recentCheckIns={checkIns}
                activityFeed={activityFeed}
                stats={stats}
                gapAnalysis={gapAnalysis}
                gapAnalysisCounts={gapAnalysisCounts}
                siteLocation={siteLocation}
                getEmployeeStats={getEmployeeStats}
                statsLoading={statsLoading}
                onCheckOut={handleManualCheckOut}
                onViewSelfie={(url) => setSelfieViewUrl(url)}
                onViewCompliance={() => {
                  setShowLiveCompliance(true);
                  setShowAnalytics(false);
                  setShowSettings(false);
                  setShowTimesheets(false);
                  setShowKiosks(false);
                }}
                alertSettings={alertSettings}
                loading={loading}
              />
            )}
          </div>
        </div>

        {/* Manual Check-In Dialog */}
        {/* QR Code Dialog */}
        <Dialog open={showQRCode} onOpenChange={setShowQRCode}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Check-In QR Code</DialogTitle>
              <DialogDescription>
                Scan this QR code to check in to {space?.name || 'this space'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 py-4">
              {siteLocation ? (
                <>
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                    <QRCodeSVG
                      id="space-checkin-qr"
                      value={`${window.location.origin}/checkin/${siteLocation.qr_code_data}`}
                      size={240}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      {space?.name}
                    </p>
                    {space?.location && (
                      <p className="text-xs text-gray-500 mt-1">
                        {space.location}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center p-8">
                  <LoadingSpinner />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                onClick={downloadQRCode}
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Download QR Code
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showManualCheckInDialog} onOpenChange={setShowManualCheckInDialog}>
          <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Manual Check-In</DialogTitle>
            <DialogDescription>
              Check in employees to this space
              {selectedUsers.length > 0 && (
                <span className="font-semibold text-blue-600 ml-2">
                  ({selectedUsers.length} selected)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {/* Search Input */}
            <div>
              <Label htmlFor="user-search">Search Employees</Label>
              <Input
                id="user-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, or department..."
                autoFocus
              />
            </div>

            {/* Select All / Deselect All */}
            {filteredUsers.filter(u => !onSiteUsers.some(c => c.user_id === u.id)).length > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {selectedUsers.length} of {filteredUsers.filter(u => !onSiteUsers.some(c => c.user_id === u.id)).length} selected
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const availableUsers = filteredUsers.filter(u => !onSiteUsers.some(c => c.user_id === u.id));
                    if (selectedUsers.length === availableUsers.length) {
                      // Deselect all
                      setSelectedUsers([]);
                    } else {
                      // Select all available users
                      setSelectedUsers(availableUsers);
                    }
                  }}
                >
                  {selectedUsers.length === filteredUsers.filter(u => !onSiteUsers.some(c => c.user_id === u.id)).length
                    ? 'Deselect All'
                    : 'Select All'}
                </Button>
              </div>
            )}

            {/* User List */}
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="font-medium">No employees found</p>
                  <p className="text-sm mt-1">
                    {searchQuery ? 'Try a different search term' : 'No employees available'}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredUsers.map((user) => {
                    const isAlreadyCheckedIn = onSiteUsers.some(c => c.user_id === user.id);
                    const isSelected = selectedUsers.some(u => u.id === user.id);
                    return (
                      <button
                        key={user.id}
                        onClick={() => !isAlreadyCheckedIn && toggleUserSelection(user)}
                        disabled={isAlreadyCheckedIn}
                        className={`w-full p-3 text-left transition-colors ${
                          isSelected
                            ? 'bg-blue-50 border-l-4 border-l-blue-600'
                            : isAlreadyCheckedIn
                            ? 'bg-gray-50 opacity-60 cursor-not-allowed'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Checkbox */}
                          {!isAlreadyCheckedIn && (
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              isSelected
                                ? 'bg-blue-600 border-blue-600'
                                : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <CheckCircle className="h-4 w-4 text-white" />
                              )}
                            </div>
                          )}

                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">
                              {user.display_name || `${user.first_name} ${user.last_name}`}
                            </p>
                            <p className="text-sm text-gray-600 truncate">
                              {user.department && <span className="text-blue-600 mr-1">{user.department}</span>}
                              {user.email}
                            </p>
                          </div>
                          {isAlreadyCheckedIn && (
                            <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                              On-Site
                            </Badge>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Notes (Optional) */}
            {selectedUsers.length > 0 && (
              <div>
                <Label htmlFor="manual-notes">Notes (Optional)</Label>
                <Textarea
                  id="manual-notes"
                  value={manualCheckInNotes}
                  onChange={(e) => setManualCheckInNotes(e.target.value)}
                  placeholder="Add notes for all selected check-ins..."
                  rows={2}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowManualCheckInDialog(false);
                setSelectedUsers([]);
                setSearchQuery('');
                setManualCheckInNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleManualCheckInSubmit}
              disabled={selectedUsers.length === 0}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Check In {selectedUsers.length > 0 && `(${selectedUsers.length})`}
            </Button>
          </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Selfie View Dialog */}
        <Dialog open={!!selfieViewUrl} onOpenChange={(open) => !open && setSelfieViewUrl(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Clock-In Selfie
              </DialogTitle>
            </DialogHeader>
            {selfieViewUrl && (
              <div className="flex justify-center py-2">
                <img
                  src={selfieViewUrl}
                  alt="Clock-in selfie"
                  className="max-w-full rounded-lg shadow-md"
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </PageTransition>
    </AppShell3>
  );
};
